import { useState } from 'react'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { Cartao } from '../../../components/ListaDeDados'
import { useSessao } from '../../../auth/contexto'
import { TEXTOS } from '../../../lib/glossario'
import type { DetalheChamado } from '../../../types/chamado'
import { LeituraDoParecer } from './ApoioDaIa'
import { useApoioDaIa } from './apoioDaIa'
import { FormularioDeMarcacao } from './marcacao/FormularioDeMarcacao'
import { Julgamento, SemJulgamento } from './marcacao/LeituraDoJulgamento'

const DESCRICAO =
  'O que o fiscal marcou sobre este chamado — é isto, e não a categoria do e-Niterói, que entra nos ' +
  'indicadores. A IA lê o chamado e sugere respostas; quem decide e assina é a fiscalização.'

/**
 * O julgamento da fiscalização e o apoio da IA, no mesmo cartão.
 *
 * **Eram dois cartões, e separá-los custava caro na prática.** O parecer da IA ficava abaixo do
 * formulário: para conferir uma resposta, o fiscal rolava a tela, procurava o trecho
 * correspondente e voltava — dezenas de vezes por mês. O resultado previsível é assinar sem ler,
 * que é o contrário do que o apoio existe para produzir. Juntos, a sugestão e a pergunta estão na
 * mesma linha de leitura, com a justificativa da IA ao lado do botão que a aceita ou recusa.
 *
 * **Juntar as telas não junta as responsabilidades.** A IA sugere e explica; a marcação continua
 * nascendo do formulário do fiscal, e o servidor grava o que o formulário mandou — nenhum valor
 * proposto tem caminho até o banco (Regra nº 3). O que fica registrado do apoio são os NOMES dos
 * campos mantidos como sugeridos, para o contrato conseguir distinguir, meses depois, julgamento
 * de assinatura.
 *
 * O gate de papel aqui é conveniência, não autorização: quem decide é o `@PreAuthorize` do
 * servidor. Esconder o botão de quem não pode usá-lo evita o clique que só produziria um 403.
 *
 * @param props o chamado
 * @returns o cartão da análise da fiscalização
 */
export function PainelDoJulgamento({ chamado }: { chamado: DetalheChamado }) {
  const [editando, setEditando] = useState(false)
  const { usuario } = useSessao()
  const podeJulgar = usuario?.papeis.some((papel) => papel === 'FISCAL' || papel === 'ADMIN') ?? false
  const marcacao = chamado.marcacaoDoFiscal
  const ia = useApoioDaIa(chamado.id)

  return (
    <Cartao
      titulo="Análise da fiscalização"
      descricao={DESCRICAO}
      acao={
        <div className="flex flex-wrap gap-2">
          {ia.disponivel && ia.podeAnalisar && (
            <Botao
              aparencia="secundario"
              tamanho="pequeno"
              onClick={() => ia.analisar.mutate()}
              disabled={ia.analisar.isPending}
            >
              {ia.analisar.isPending
                ? TEXTOS.analiseIa.analisando
                : ia.parecer
                  ? TEXTOS.analiseIa.refazer
                  : TEXTOS.analiseIa.botao}
            </Botao>
          )}
          {podeJulgar && !editando && (
            <Botao tamanho="pequeno" onClick={() => setEditando(true)}>
              {marcacao ? TEXTOS.marcacao.editar : TEXTOS.marcacao.abrir}
            </Botao>
          )}
        </div>
      }
    >
      <div className="space-y-5">
        {ia.disponivel && <ApoioDaIa ia={ia} />}

        {editando ? (
          <FormularioDeMarcacao
            chamadoId={chamado.id}
            competencia={chamado.competencia}
            marcacao={marcacao}
            parecer={ia.parecerUtil}
            aoConcluir={() => setEditando(false)}
            aoCancelar={() => setEditando(false)}
          />
        ) : (
          <div className="space-y-4">
            {marcacao ? (
              <Julgamento marcacao={marcacao} categoriaDoENiteroi={chamado.categoria} />
            ) : (
              <SemJulgamento />
            )}
            {!podeJulgar && !marcacao && (
              <p className="max-w-prose text-xs text-slate-500">{TEXTOS.marcacao.semPapel}</p>
            )}
          </div>
        )}
      </div>
    </Cartao>
  )
}

/**
 * A leitura da IA, dentro do cartão.
 *
 * Fica acima do formulário porque é o que se lê primeiro: o resumo do chamado e o que merece
 * conferência. As sugestões campo a campo aparecem depois, coladas em cada pergunta.
 *
 * @param props o estado do apoio da IA
 * @returns o bloco da leitura automática
 */
function ApoioDaIa({ ia }: { ia: ReturnType<typeof useApoioDaIa> }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-slate-50/70 p-4" aria-label={TEXTOS.analiseIa.titulo}>
      <h3 className="text-sm font-semibold text-slate-800">{TEXTOS.analiseIa.titulo}</h3>
      <p className="mt-0.5 max-w-3xl text-xs text-slate-500">{TEXTOS.analiseIa.ajuda}</p>

      <div className="mt-3 space-y-3">
        {ia.analisar.isError && <AvisoDeErro erro={ia.analisar.error} titulo={TEXTOS.analiseIa.falhou} />}
        {ia.carregando && <Esqueleto linhas={2} />}
        {!ia.carregando && <EstadoDaLeitura ia={ia} />}
        {ia.parecer && <LeituraDoParecer parecer={ia.parecer} />}
      </div>
    </section>
  )
}

/**
 * A frase que explica em que pé está a leitura — e por que ela está assim.
 *
 * PRONTO não diz nada: o parecer abaixo fala por si, e uma tarja "está pronto" em cima de um texto
 * pronto é ruído. Os outros seis dizem, porque "nenhuma análise feita" cobria cinco situações com
 * respostas diferentes — esperar, pedir pelo botão, ou não esperar nada.
 *
 * O `motivo` vem do servidor em pt-BR e é mais específico que o texto do estado (qual trava
 * segurou, qual teto foi atingido); quando ele vem, é ele que aparece.
 *
 * @param props o estado do apoio da IA
 * @returns a frase do estado, ou nada quando a leitura está pronta
 */
function EstadoDaLeitura({ ia }: { ia: ReturnType<typeof useApoioDaIa> }) {
  if (!ia.estado || ia.estado === 'PRONTO') {
    return null
  }
  const texto = ia.motivo ?? TEXTOS.analiseIa.estado[ia.estado]
  return <p className="max-w-prose text-sm text-slate-500">{texto}</p>
}
